import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import type {
  ComponentProps,
  DragEvent,
  HTMLAttributes,
  ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  Position,
  ScoreStatus,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPen } from '@sorare/core/src/atoms/icons/system/iconPen.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { OverlapItems } from '@sorare/core/src/atoms/ui/OverlapItems';
import { PlayerAppearance } from '@sorare/core/src/components/cards/PlayerAppearance';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { Captain } from '@sorare/core/src/components/so5/Captain';
import { StatIcon } from '@sorare/core/src/components/stats/StatIcon';
import { range } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { Stats } from '@sorare/core/src/lib/scoring';
import { ScheduledPlayerGameScoreState } from '@sorare/marketplace/src/components/game/ScheduledPlayerGameScoreState';
import {
  isSubstitutedIn,
  isSubstitutedOut,
} from '@sorare/marketplace/src/lib/playingStatus';
import { isGameLive } from '@sorare/marketplace/src/shared/game/lib';
import { SubIcon } from '@sorare/marketplace/src/shared/stats/SubIcon';
import { getDecisiveActions } from '@sorare/marketplace/src/shared/stats/lib';

import { So5AppearanceScore } from '#src/components/appearance/So5AppearanceScore/index.tsx';
import { NewMVPWrapper } from '#src/components/card/MVPWrapper/index.tsx';
import { So5AppearenceInlineGames } from '#src/components/stats/So5AppearenceInlineGames/index.tsx';
import { So5AppearenceInlineGamesDots } from '#src/components/stats/So5AppearenceInlineGamesDots/index.tsx';
import { isAppearanceLocked } from '#src/lib/so5.ts';

import type {
  PlayerCard_appearanceInterface,
  PlayerCard_composeTeamBenchObjectInterface,
} from './__generated__/index.graphql.ts';

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
`;

const EditIconWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  right: calc(-1 * var(--unit));
  z-index: 1;
`;

const SlotFooter = styled.div`
  display: grid;
  grid-template-rows: var(--triple-unit) var(--double-and-a-half-unit);
  gap: var(--half-unit);
  justify-items: center;
`;

const MVPFrameOnCardOnly = styled(NewMVPWrapper)<{ $visible: boolean }>`
  pointer-events: none;

  > div:last-child {
    pointer-events: auto;
  }

  > div:first-child {
    bottom: calc(6 * var(--unit));
    pointer-events: none;
    user-select: none;
    opacity: ${props => (props.$visible ? 1 : 0)};
  }
`;

const DragWrapper = styled(Vertical).attrs({ center: true })`
  width: 100%;
`;

type Props = {
  onClick?: () => void;
  appearance: PlayerCard_composeTeamBenchObjectInterface;
  showScore?: boolean;
  showAverageScore?: boolean;
  isMVP?: boolean;
  showMVPFrame?: boolean;
  withDate?: boolean;
  withInlineGamesDots?: boolean;
  displayLiveEdit?: boolean;
  editPath?: string;
  injuryTagNode?: ReactNode;
  gradeNode?: ReactNode;
  dragProps?: ComponentProps<typeof PlayerAppearance>['dragProps'];
};

export const PlayerCard = ({
  onClick,
  appearance,
  showScore,
  showAverageScore,
  isMVP,
  showMVPFrame,
  withDate,
  withInlineGamesDots,
  displayLiveEdit,
  injuryTagNode,
  gradeNode,
  editPath,
  dragProps,
}: Props) => {
  const { formatMessage } = useIntl();
  const dragPreviewRef = useRef<HTMLDivElement>(null);
  const [showMVPFrameForDragImage, setShowMVPFrameForDragImage] =
    useState(false);

  const handleDragStartWithPreview = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      if (isMVP && !showMVPFrame) {
        flushSync(() => {
          setShowMVPFrameForDragImage(true);
        });
      }
      const dragPreviewElement = dragPreviewRef.current;
      if (dragPreviewElement) {
        try {
          const { left, top } = dragPreviewElement.getBoundingClientRect();
          event.dataTransfer.setDragImage(
            dragPreviewElement,
            event.clientX - left,
            event.clientY - top
          );
        } catch {
          // Fall back to browser default drag image when setDragImage fails.
        }
      }
      dragProps?.onDragStart?.(event);
    },
    [isMVP, showMVPFrame, dragProps]
  );

  const handleDragEndWithPreview = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      setShowMVPFrameForDragImage(false);
      dragProps?.onDragEnd?.(event);
    },
    [dragProps]
  );

  const nbaAverageScore = appearance.nbaAverageScore ?? null;

  if (
    !isType(appearance, 'So5Appearance') &&
    !isType(appearance, 'ThresholdPickerTaskAppearance')
  ) {
    return null;
  }

  const {
    eligiblePlayerGameScores,
    captain,
    anyPlayer,
    lockedForLeaderboard,
    playedGames,
  } = appearance;

  const anyGameLive = eligiblePlayerGameScores.some(pgs =>
    isGameLive(pgs.anyGame.status)
  );

  // the player card may be SP but the player may be a relief pitcher today
  // they can still be used and score pitching points in the SP slot but they are in fact a relief pitcher
  const isStartingPitcher =
    eligiblePlayerGameScores.some(
      pgs => pgs.positionTyped === Position.baseball_starting_pitcher
    ) && anyPlayer.anyPositions.includes(Position.baseball_starting_pitcher);

  const pitchingGames = eligiblePlayerGameScores.filter(
    pgs =>
      isType(pgs.anyGame, 'GameOfBaseball') &&
      pgs.anyGame.probablePitchers.find(p => p.slug === anyPlayer.slug)
  );

  // player is technically not DNP but we can confidently assume they will be
  const scoreIsPendingButWillBeDNP =
    isStartingPitcher && pitchingGames.length === 0 && playedGames.length === 0;

  const uniquePlayerGameScore = appearance.playerGameScore;
  const decisiveActions = uniquePlayerGameScore
    ? getDecisiveActions(uniquePlayerGameScore)
    : [];
  const hasDecisiveActions = Object.values(decisiveActions).some(Boolean);

  const isLocked = isType(appearance, 'So5Appearance')
    ? isAppearanceLocked(appearance)
    : false;
  const isDraggable = !!dragProps?.draggable;
  const dragPropsWithPreview = isDraggable
    ? {
        ...dragProps,
        onDragStart: handleDragStartWithPreview,
        onDragEnd: handleDragEndWithPreview,
      }
    : undefined;

  const appearanceNode = (
    <PlayerAppearance
      onClick={onClick}
      disabled={!eligiblePlayerGameScores.length}
      displayLocked={!!displayLiveEdit && Boolean(isLocked)}
      appearance={appearance}
      captain={!!captain && <Captain active size={2} />}
      dragProps={dragPropsWithPreview}
      topBadge={
        <>
          {gradeNode}
          {!!isSubstitutedOut(uniquePlayerGameScore) && (
            <SubIcon type="sub_out" />
          )}
          {!!isSubstitutedIn(uniquePlayerGameScore) && (
            <SubIcon type="sub_in" />
          )}
        </>
      }
      bottomBadge={
        <Horizontal center>
          {injuryTagNode}
          {!!hasDecisiveActions && (
            <OverlapItems offset={8}>
              {Object.entries(decisiveActions).map(([key, value]) =>
                range(value || 0).map(i => (
                  <StatIcon key={`${key}-${i}`} statKey={key as Stats} />
                ))
              )}
            </OverlapItems>
          )}
          <ScheduledPlayerGameScoreState
            hidePredictions={!!lockedForLeaderboard}
            playerGameScores={eligiblePlayerGameScores}
            variant="chip"
          />
        </Horizontal>
      }
      score={
        <SlotFooter>
          <Horizontal gap={0.5}>
            {showAverageScore && typeof nbaAverageScore === 'number' ? (
              <Score
                hideReviewing
                score={nbaAverageScore}
                scoreStatus={ScoreStatus.FINAL}
                scoreType="PLAYER_SCORE"
                shape="HEXAGON"
                animated={false}
                size={4}
              />
            ) : (
              <>
                {!showScore && !!anyGameLive && !withInlineGamesDots && (
                  <LiveDot size="sm" animate />
                )}
                {!!showScore && (
                  <Horizontal gap={0.5}>
                    {!!anyGameLive && !withInlineGamesDots && (
                      <LiveDot size="sm" animate />
                    )}
                    <So5AppearanceScore
                      hideReviewing
                      withDate={withDate}
                      so5Appearance={appearance}
                      forceScoreStatus={
                        scoreIsPendingButWillBeDNP
                          ? ScoreStatus.DID_NOT_PLAY
                          : undefined
                      }
                    />
                  </Horizontal>
                )}
              </>
            )}
          </Horizontal>
          {withInlineGamesDots ? (
            <So5AppearenceInlineGamesDots
              isStartingPitcher={isStartingPitcher}
              sizeUnit={anyPlayer.sport === Sport.BASEBALL ? 'md' : 'lg'}
              pitchingGames={pitchingGames}
              so5Appearance={appearance}
            />
          ) : (
            <So5AppearenceInlineGames so5Appearance={appearance} />
          )}
        </SlotFooter>
      }
    />
  );

  const showMVPFrameOnCard = !!showMVPFrame || showMVPFrameForDragImage;
  const cardContent = isMVP ? (
    <MVPFrameOnCardOnly $visible={showMVPFrameOnCard}>
      {appearanceNode}
    </MVPFrameOnCardOnly>
  ) : (
    appearanceNode
  );

  return (
    <Wrapper>
      {isDraggable ? (
        // PlayerAppearanceDragProps target <button>; DragWrapper is a <div>.
        <DragWrapper
          ref={dragPreviewRef}
          {...(dragPropsWithPreview as unknown as HTMLAttributes<HTMLDivElement>)}
        >
          {cardContent}
        </DragWrapper>
      ) : (
        cardContent
      )}
      {!!displayLiveEdit && !isLocked && (
        <EditIconWrapper>
          <IconButton
            size="compact"
            color="tertiary"
            aria-label={formatMessage(glossary.edit)}
            to={editPath}
          >
            <FontAwesomeIcon icon={iconPen} size="xs" />
          </IconButton>
        </EditIconWrapper>
      )}
    </Wrapper>
  );
};

const appearanceInterfaceFragment = gql`
  fragment PlayerCard_appearanceInterface on AppearanceInterface {
    id
    captain
    playedGames {
      id
    }
    playerGameScore {
      id
      scoreStatus
      anyGame {
        id
        status: statusTyped
      }
      ...isSubstitutedIn_playerGameScore
      ...isSubstitutedOut_playerGameScore
      ...getDecisiveActions_playerGameScoreInterface
    }
    ...So5AppearanceScore_so5Appearance
    ...So5AppearenceInlineGames_so5Appearance
    ...So5AppearenceInlineGamesDots_so5Appearance
  }
  ${isSubstitutedIn.fragments.playerGameScore}
  ${isSubstitutedOut.fragments.playerGameScore}
  ${getDecisiveActions.fragments.playerGameScoreInterface}
  ${So5AppearanceScore.fragments.so5Appearance}
  ${So5AppearenceInlineGames.fragments.so5Appearance}
  ${So5AppearenceInlineGamesDots.fragments.so5Appearance}
` as TypedDocumentNode<PlayerCard_appearanceInterface>;

PlayerCard.fragments = {
  composeTeamBenchObjectInterface: gql`
    fragment PlayerCard_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      lockedForLeaderboard
      nbaAverageScore: averageScore(type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE)
      anyPlayer {
        slug
        sport
        anyPositions
      }
      eligiblePlayerGameScores {
        id
        positionTyped
        anyGame {
          id
          status: statusTyped
          ... on GameOfBaseball {
            id
            probablePitchers {
              slug
            }
          }
        }
        ...ScheduledPlayerGameScoreState_playerGameScore
      }
      ... on AppearanceInterface {
        id
        ...PlayerCard_appearanceInterface
      }
      ... on So5Appearance {
        id
        ...isAppearanceLocked_so5Appearance
      }

      ...PlayerAppearance_composeTeamBenchObjectInterface
    }
    ${PlayerAppearance.fragments.composeTeamBenchObjectInterface}
    ${isAppearanceLocked.fragments.so5Appearance}
    ${ScheduledPlayerGameScoreState.fragments.playerGameScore}
    ${appearanceInterfaceFragment}
  ` as TypedDocumentNode<PlayerCard_composeTeamBenchObjectInterface>,
};
